import { OBJECT_EDGES } from '@/constants/object';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { render, updateModelOutlinePass, updateSphereEdgePass } from '@/threejs/core';
import { FatLinesBatch } from '@/threejs/core/FatLinesBatch';
import { useEventListener } from '@vueuse/core';
import * as THREE from 'three';

/**
 * EdgesManager - Centralized management for object edge rendering
 * Handles edge visibility, color, thickness, and global double-sided material settings
 * Integrates FatLinesBatch (FLB) system for efficient edge rendering
 */
class EdgesManager {
  constructor() {
    this.showObjectEdges = OBJECT_EDGES.SHOW;
    this.edgeColor = OBJECT_EDGES.COLOUR;
    this.edgeThickness = OBJECT_EDGES.THICKNESS;
    this.globalDoubleSided = false;
    this.isInitialized = false;

    // FatLinesBatch (FLB) properties
    this.scene = null;
    this.flb = null;
    this.flbMeshMapping = [];
    this._selectedObjectCode = null;

    // Selection outline FLB (3D edges for selected object, always visible)
    this.selectionFlb = null;
    this.selectionColor = OBJECT_EDGES.SELECTION_COLOR;

    // Lazy initialization of store to avoid calling before Pinia is ready
    this._cruxModuleStore = null;

    // Debounce timer for refreshFlb
    this._refreshFlbTimer = null;
  }

  /**
   * Initialize the edges manager with scene reference
   * @param {THREE.Scene} scene - The Three.js scene to manage edges for
   */
  initialize() {
    this.isInitialized = true;
    return this;
  }

  initializeEdges(scene) {
    if (!scene) {
      throw new Error('EdgesManager requires a scene to initialize');
    }

    this.scene = scene;

    // Set up event listeners for object changes using VueUse
    // Add/remove are debounced to avoid rebuilding FLB N times during batch operations (e.g. scene load)
    // Update is immediate so visibility changes don't lag behind the object
    useEventListener(window, 'objectAdded', () => {
      this.debouncedRefreshFlb();
    });

    useEventListener(window, 'objectUpdated', () => {
      this.refreshFlb();
    });

    useEventListener(window, 'objectRemoved', () => {
      this.debouncedRefreshFlb();
    });
  }
  /**
   * Check if manager is initialized
   * @returns {boolean}
   */
  isManagerInitialized() {
    return this.isInitialized;
  }

  /**
   * Get crux module store (lazy initialization)
   * @returns {object} Crux module store
   */
  getCruxModuleStore() {
    if (!this._cruxModuleStore) {
      this._cruxModuleStore = useCruxModuleStore();
    }
    return this._cruxModuleStore;
  }

  /**
   * Get view setting value
   * @param {string} key - Setting key
   * @param {*} defaultValue - Default value if setting not found
   * @returns {*} Setting value
   */
  getViewSetting(key, defaultValue = null) {
    return this.getCruxModuleStore().getSettingValue('viewport_settings', key, defaultValue);
  }

  /**
   * Set object edges visibility
   * @param {boolean} visible - Whether object edges should be visible
   */
  setObjectEdgesVisible(visible) {
    this.showObjectEdges = visible;
    this.refreshFlb();
    render();
  }

  /**
   * Set object edge color
   * @param {string|number} color - Color for object edges
   */
  setObjectEdgeColor(color) {
    color = color.startsWith('#') ? color : `#${color}`;
    this.edgeColor = color;
    this.setFlbEdgeColor(color);

    // Update custom edges (cube edge lines)
    if (this.scene) {
      this.scene.traverse((child) => {
        if (child.userData.isCubeEdge) {
          child.material.color.set(color);
        }
      });
    }

    // Update sphere edge outline pass color
    updateSphereEdgePass(this.getViewSetting('render.showObjectEdges', true), color, null, this.edgeThickness);

    render();
  }

  /**
   * Set object edge thickness
   * @param {number} thickness - Thickness for object edges
   */
  setObjectEdgeThickness(thickness) {
    this.edgeThickness = thickness;
    this.refreshFlb();
  }

  /**
   * Turn on FatLinesBatch edges
   */
  turnOnFlb() {
    if (!this.scene) {
      console.warn('EdgesManager: Scene not initialized');
      return;
    }

    this.turnOffFlb();

    let gs = [];
    this.flbMeshMapping = []; // Reset mesh mapping

    this.scene.traverse((child) => {
      child.updateMatrixWorld();
      // Skip internal Three.js objects (lights, cameras, etc.)
      if (child.isLight || child.isCamera) return;
      // Skip explicitly excluded objects
      if (child.userData.excludeFromOutline) return;

      if (child.visible === false) return;

      // Check if any parent is hidden
      let p = child.parent;
      while (p) {
        if (p.visible === false) return;
        p = p.parent;
      }

      // Skip selected object — handled by selection FLB
      if (this._selectedObjectCode) {
        let current = child;
        while (current) {
          if (current.userData?.code === this._selectedObjectCode) return;
          current = current.parent;
        }
      }

      if (child.userData.edgeOutline) {
        if (child.isMesh) {
          gs.push(child.geometry.clone().applyMatrix4(child.matrixWorld));
          this.flbMeshMapping.push(child); // Store reference to the mesh
        }
      }
    });

    if (gs.length > 0) {
      const edgeColor = this.getViewSetting('render.objectEdgeColour', this.edgeColor);
      const edgeThickness = this.getViewSetting('render.objectEdgeThickness', this.edgeThickness);

      this.flb = new FatLinesBatch(gs, {
        color: edgeColor,
        thickness: edgeThickness,
      });

      // Dispose cloned input geometries — FatLinesBatch has consumed them
      gs.forEach((g) => g.dispose());

      // Check if FatLinesBatch was successfully created with valid edges
      if (this.flb.items && this.flb.items.length > 0 && this.flb.colorIdx && this.flb.colorIdx.length > 0) {
        this.flb.items.forEach((it, idx) => {
          let color = edgeColor.startsWith('#') ? edgeColor : `#${edgeColor}`;
          this.flb.setColorAt(idx, color);
        });
        this.flb.update();
        this.scene.add(this.flb);
      } else {
        console.warn('No valid edges to display for outlined objects');
        this.flb.dispose();
        this.flb = null;
      }
    }
  }

  /**
   * Turn off FatLinesBatch edges
   */
  turnOffFlb() {
    if (this.flb && this.scene) {
      this.scene.remove(this.flb);
      this.flb.dispose();
      this.flb = null;
    }
    this.flbMeshMapping = [];
  }

  /**
   * Debounced version of refreshFlb for event listeners
   */
  debouncedRefreshFlb() {
    if (this._refreshFlbTimer) clearTimeout(this._refreshFlbTimer);
    this._refreshFlbTimer = setTimeout(() => {
      this._refreshFlbTimer = null;
      this.refreshFlb();
    }, 50);
  }

  /**
   * Refresh FatLinesBatch edges based on current settings
   */
  refreshFlb() {
    if (!this.scene) {
      console.warn('EdgesManager: Scene not initialized');
      return;
    }

    const showEdges = this.getViewSetting('render.showObjectEdges', true);
    if (showEdges) {
      this.turnOnFlb();
    } else {
      this.turnOffFlb();
    }

    // Toggle custom edges (cube edge lines)
    this.scene.traverse((child) => {
      if (child.userData.isCubeEdge) {
        child.visible = showEdges;
      }
    });

    // Update sphere/LED edge outline pass — exclude selected object (selection outline handles it)
    const edgeColor = this.getViewSetting('render.objectEdgeColour', this.edgeColor);
    updateSphereEdgePass(showEdges, edgeColor, this._selectedObjectCode, this.edgeThickness);

    // Rebuild selection outline to follow transforms
    this.updateSelectionOutline();

    render();
  }

  /**
   * Update 3D selection outline for the given object
   * @param {string|null} objectCode - The code of the selected object, or null to clear
   */
  hideEdgesForSelected(objectCode) {
    this._selectedObjectCode = objectCode;
    // Rebuild main FLB (excludes selected) and selection FLB (only selected)
    this.refreshFlb();
  }

  /**
   * Build a 3D FLB outline for the currently selected object
   */
  updateSelectionOutline() {
    // Remove previous selection outline
    if (this.selectionFlb && this.scene) {
      this.scene.remove(this.selectionFlb);
      this.selectionFlb = null;
    }

    // Clear 2D model outline by default
    updateModelOutlinePass(null);

    if (!this._selectedObjectCode || !this.scene) return;

    // Skip selection outlines entirely for annotations
    let skipSelectionOutline = false;
    // Check if the selected object should use 2D outline pass instead of 3D FLB
    let use2DOutline = false;
    // Check if the selected object is hidden
    let selectedObjectHidden = false;
    this.scene.traverse((child) => {
      if (child.userData?.code === this._selectedObjectCode) {
        if (child.userData?.type === 'annotation') {
          skipSelectionOutline = true;
        }
        if (child.userData?.type === 'model' || child.userData?.type === 'led_screen' || child.userData?.isSphere) {
          use2DOutline = true;
        }
        if (child.visible === false) {
          selectedObjectHidden = true;
        }
      }
    });

    if (skipSelectionOutline || selectedObjectHidden) return;

    // Custom 3D models and LED screens use 2D outline pass instead of 3D FLB
    if (use2DOutline) {
      updateModelOutlinePass(this._selectedObjectCode, this.selectionColor);
      render();
      return;
    }

    // Collect geometries from the selected object's meshes
    let gs = [];
    this.scene.traverse((child) => {
      if (!child.isMesh || child.visible === false) return;
      if (child.userData.excludeFromOutline) return;

      // Check if any parent is hidden
      let anyParentHidden = false;
      let current = child;
      let belongsToSelected = false;
      while (current) {
        if (current.visible === false) {
          anyParentHidden = true;
          break;
        }
        if (current.userData?.code === this._selectedObjectCode) {
          belongsToSelected = true;
        }
        current = current.parent;
      }

      if (belongsToSelected && !anyParentHidden && child.geometry) {
        child.updateMatrixWorld();
        gs.push(child.geometry.clone().applyMatrix4(child.matrixWorld));
      }
    });

    if (gs.length === 0) return;

    this.selectionFlb = new FatLinesBatch(gs, {
      color: this.selectionColor,
      thickness: 1.5,
      edgeThresholdAngle: 10,
      depthTest: false,
      renderOrder: 9,
    });

    // Dispose cloned input geometries — FatLinesBatch has consumed them
    gs.forEach((g) => g.dispose());

    if (
      this.selectionFlb.items &&
      this.selectionFlb.items.length > 0 &&
      this.selectionFlb.colorIdx &&
      this.selectionFlb.colorIdx.length > 0
    ) {
      this.selectionFlb.items.forEach((it, idx) => {
        this.selectionFlb.setColorAt(idx, this.selectionColor);
      });
      this.selectionFlb.update();
      this.scene.add(this.selectionFlb);
    } else {
      this.selectionFlb.dispose();
      this.selectionFlb = null;
    }

    render();
  }

  /**
   * Set FLB edge color
   * @param {string|number} color - Color for FLB edges
   */
  setFlbEdgeColor(color) {
    if (this.flb) {
      color = color.startsWith('#') ? color : `#${color}`;
      this.flb.setEdgeColor(color);
    }
    render();
  }

  /**
   * Set FLB edge thickness
   * @param {number} thickness - Thickness for FLB edges
   */
  setFlbEdgeThickness(thickness) {
    if (this.flb) {
      this.flb.setEdgeThickness(thickness);
    }
    render();
  }

  /**
   * Set global double-sided material mode
   * @param {boolean} doubleSided - Whether all materials should be double-sided
   */
  setGlobalDoubleSided(doubleSided) {
    this.globalDoubleSided = doubleSided;
    if (this.scene) {
      this.scene.traverse((child) => {
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((material) => {
              material.side = doubleSided ? THREE.DoubleSide : THREE.FrontSide;
              material.needsUpdate = true;
            });
          } else {
            child.material.side = doubleSided ? THREE.DoubleSide : THREE.FrontSide;
            child.material.needsUpdate = true;
          }
        }
      });
    }

    render();
  }

  /**
   * Apply edge settings to a specific object
   * @param {THREE.Object3D} object - Object to apply edge settings to
   */
  applyEdgeSettingsToObject(object) {
    if (!object || !object.material) {
      return;
    }

    // Apply double-sided setting
    if (Array.isArray(object.material)) {
      object.material.forEach((material) => {
        material.side = this.globalDoubleSided ? THREE.DoubleSide : THREE.FrontSide;
        material.needsUpdate = true;
      });
    } else {
      object.material.side = this.globalDoubleSided ? THREE.DoubleSide : THREE.FrontSide;
      object.material.needsUpdate = true;
    }

    // If edges are enabled, this would trigger the edge geometry update
    if (this.showObjectEdges) {
      // This would integrate with your edge geometry system
      this.updateObjectEdges(object);
    }
  }

  /**
   * Update edge geometry for a specific object
   * @param {THREE.Object3D} object - Object to update edges for
   */
  updateObjectEdges(object) {
    // This is where you would implement object-specific edge updates
    // The actual implementation depends on your edge rendering system

    // Example implementation - adjust according to your system:
    if (object.geometry && this.showObjectEdges) {
      // Remove existing edge geometry if it exists
      const existingEdges = object.getObjectByName('edges');
      if (existingEdges) {
        object.remove(existingEdges);
        existingEdges.geometry?.dispose();
        existingEdges.material?.dispose();
      }

      // Create new edge geometry
      const edges = new THREE.EdgesGeometry(object.geometry);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: this.edgeColor,
        linewidth: this.edgeThickness,
      });
      const edgeLines = new THREE.LineSegments(edges, lineMaterial);
      edgeLines.name = 'edges';
      object.add(edgeLines);
    }
  }

  /**
   * Remove edges from all objects
   */
  removeAllEdges() {
    if (this.scene) {
      this.scene.traverse((child) => {
        const edges = child.getObjectByName('edges');
        if (edges) {
          child.remove(edges);
          edges.geometry?.dispose();
          edges.material?.dispose();
        }
      });
    }
  }

  /**
   * Handle edges setting changes
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   */
  handleEdgesSetting(key, value) {
    switch (key) {
      case 'render.showObjectEdges':
        this.setObjectEdgesVisible(value);
        break;
      case 'render.objectEdgeColour':
        this.setObjectEdgeColor(value);
        break;
      case 'render.objectEdgeThickness':
        this.setObjectEdgeThickness(value);
        break;
      case 'render.globalDoubleSided':
        this.setGlobalDoubleSided(value);
        break;
      default:
        console.warn(`Unknown edges setting: ${key}`);
    }
  }

  /**
   * Get current edges configuration
   * @returns {object} Current edges settings
   */
  getEdgesConfig() {
    return {
      showObjectEdges: this.showObjectEdges,
      edgeColor: this.edgeColor,
      edgeThickness: this.edgeThickness,
      globalDoubleSided: this.globalDoubleSided,
    };
  }

  /**
   * Dispose of edges resources
   */
  dispose() {
    if (this._refreshFlbTimer) {
      clearTimeout(this._refreshFlbTimer);
      this._refreshFlbTimer = null;
    }
    this.removeAllEdges();
    this.turnOffFlb();
    if (this.selectionFlb && this.scene) {
      this.scene.remove(this.selectionFlb);
      this.selectionFlb.dispose();
      this.selectionFlb = null;
    }
    updateModelOutlinePass(null);
    this.scene = null;
    this._cruxModuleStore = null;
    this.isInitialized = false;
  }
}

// Create and export singleton instance
export const edgesManager = new EdgesManager();

// Export the class for testing or multiple instances if needed
export { EdgesManager };
